import { useEffect, useState } from "react";
import { Loader2, Search, Phone, MessageCircle, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { waLink } from "@/pages/admin/AdminBuyers";
import { MatchesModal } from "@/components/admin/MatchesModal";
import { toast } from "sonner";

const STATUSES = ["New","Active","Matched","Contacted","Negotiation","Site Visit","Deal In Process","Purchased","Not Interested","Closed"];
const COLORS = {
  New: "bg-orange-100 text-orange-700", Active: "bg-blue-100 text-blue-700",
  Matched: "bg-indigo-100 text-indigo-700", Contacted: "bg-cyan-100 text-cyan-700",
  Negotiation: "bg-purple-100 text-purple-700", "Site Visit": "bg-amber-100 text-amber-700",
  "Deal In Process": "bg-violet-100 text-violet-700", Purchased: "bg-emerald-100 text-emerald-700",
  "Not Interested": "bg-slate-100 text-slate-500", Closed: "bg-slate-100 text-slate-500",
};
const telLink = (n) => (n ? `tel:${n.replace(/[^\d+]/g, "")}` : null);
const fmtBudget = (a, b) => {
  if (!a && !b) return "—";
  const f = (n) => `₹${new Intl.NumberFormat("en-IN").format(n)}`;
  if (a && b) return `${f(a)} – ${f(b)}`;
  return f(a || b);
};

export default function AdminRequirements() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [matchReq, setMatchReq] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/admin/requirements", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, status]);

  const setReqStatus = async (id, s) => {
    try { await api.patch(`/admin/requirements/${id}/status`, { status: s }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Buyer Requirements</h1>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="req-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input data-testid="req-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search machine, brand, category, REQ ID…" className="pl-9" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {items.length === 0 && <p className="py-12 text-center text-slate-500">No requirements yet.</p>}
          {items.map((r) => (
            <div key={r.id} data-testid={`req-row-${r.req_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{r.req_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${COLORS[r.status]}`}>{r.status}</span>
                    {r.urgency && r.urgency !== "Normal" && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600">{r.urgency}</span>}
                    {r.match_count > 0 && <span data-testid={`req-match-count-${r.req_code}`} className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">⚡ {r.match_count} machine{r.match_count > 1 ? "s" : ""} matched</span>}
                  </div>
                  <p className="mt-0.5 font-semibold text-slate-900">{r.machine_name || r.category_name || "Requirement"}{r.brand ? ` • ${r.brand}` : ""}{r.model ? ` ${r.model}` : ""}</p>
                  <p className="text-sm text-slate-500">{r.category_name} • {r.machine_type} • Budget {fmtBudget(r.budget_min, r.budget_max)} • Qty {r.quantity}</p>
                  {r.preferred_location && <p className="text-sm text-slate-500">📍 {r.preferred_location}</p>}
                  <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Buyer:</span> {r.buyer_name} <span className="font-mono text-xs text-slate-400">{r.buyer_code}</span> • {r.buyer_mobile}</p>
                  {r.notes && <p className="mt-1 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">"{r.notes}"</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="w-40">
                    <Select value={r.status} onValueChange={(v) => setReqStatus(r.id, v)}>
                      <SelectTrigger data-testid={`req-status-${r.req_code}`} className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`find-matches-${r.req_code}`} onClick={() => setMatchReq(r)} title="Find matching machines" className="rounded-lg border border-orange-200 bg-orange-50 p-2 text-orange-600"><Zap size={15} /></button>
                    {telLink(r.buyer_mobile) && <a href={telLink(r.buyer_mobile)} className="rounded-lg border border-slate-200 p-2 text-slate-600"><Phone size={15} /></a>}
                    {waLink(r.buyer_whatsapp) && <a href={waLink(r.buyer_whatsapp)} target="_blank" rel="noreferrer" className="rounded-lg bg-whatsapp p-2 text-white"><MessageCircle size={15} /></a>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {matchReq && <MatchesModal mode="machines" source={{ id: matchReq.id, label: `${matchReq.req_code} · ${matchReq.machine_name || matchReq.category_name || "Requirement"}` }} onClose={() => setMatchReq(null)} />}
    </div>
  );
}
